/**
 * Wire schemas for the Operations **Closed** surface — the graveyard: every
 * case that has reached Case Closed, the funnel's terminal node.
 *
 * Closed is the terminal sink for the Settled exit. A case leaves the
 * **Settled book** the moment its winning placement flips
 * Decision = Settled → Case Closed; Closed is where it lands. The two
 * surfaces are filters on the *same* Placement Decision field, so the
 * handoff is exact — a case physically cannot fall between them. That is the
 * point of this surface: when a matter disappears from Settled book, it is
 * visibly accounted for here. It is a census of the dead, not a worklist —
 * nothing is acted on, there is no clock and no ordering by urgency.
 *
 * Each row carries only what identifies a closed matter: the client, the
 * money it settled for, the two firms (OF + HF), and the date it closed.
 * Deliberately NOT here: days-since-closed (a closed matter has no live
 * clock), the disbursement spine, and the fee split (the disbursement state
 * was Settled book's job; here the case is done). A search box and filters
 * (OF / HF / case type / state) are a deferred later pass — the closed book
 * is a few dozen today, small enough to read flat.
 *
 * The keying: the population is **placements at Decision = Case Closed**
 * (the same field Settled book watches), joined to their lead for the client
 * identity. A closed placement whose lead is NOT resolvable at Lead Status
 * "Case Closed" — the status didn't flip (a Brian-Hanis-class off-path
 * record) or the link is broken — is flagged `statusAnomaly` and **shown,
 * not dropped**: a silent drop is the one failure a graveyard cannot have.
 *
 * All derivation is read-only and live — no stored Closed state.
 *
 * Spec: engineering/command-center/docs/port-notes/closed.md (evicted 2026-08-29, #775 round 10)
 */

import { z } from "zod";
import {
  PANEL_DETAIL_LEAD_FIELDS,
  PLACEMENT_DETAIL_FIELDS,
} from "./placement-detail.js";

/**
 * One closed matter. The unit is the **placement** that closed (Decision =
 * Case Closed); its lead supplies the client identity. The composer does the
 * join so the UI renders without its own lookups.
 *
 * Identity fields are nullable because the graveyard never drops a closed
 * placement: when its lead can't be resolved (status didn't flip, or the link
 * is broken), `client` / `leadId` come back null, `statusAnomaly` is true, and
 * the row still renders off the placement's own data (amount, HF, close date)
 * plus whatever lead record id the link carried — enough to go investigate.
 */
export const ClosedCaseSchema = z.object({
  /** The closed placement's record ID — the row's identity here. */
  id: z.string(),
  /** Linked Lead record ID off the placement, or null when the link is
   *  broken. Present even for an anomalous row (the thread to pull). */
  leadRecordId: z.string().nullable(),
  /** Stable human-readable Lead ID, or null when the lead isn't resolved. */
  leadId: z.string().nullable(),
  /** Client name, or null when the lead isn't resolved (an anomaly). */
  client: z.string().nullable(),

  /** Joined Originating Firm name (the OF that referred the matter), or null.
   *  When the OF is LegalFlare this reads "LegalFlare". */
  originatingFirmName: z.string().nullable(),
  /** Joined Handling Firm name (the firm that handled the case), or null. */
  handlingFirmName: z.string().nullable(),

  /** Settlement amount the HF reported, or null when not recorded. */
  settlementAmount: z.number().nullable(),
  /** Case Closed Date (ISO date) off the placement — the date the matter
   *  closed. Null is itself an anomaly (closed but undated); sorts last. */
  caseClosedDate: z.string().nullable(),

  /** TRUE when this closed placement's lead could not be resolved at Lead
   *  Status "Case Closed" — the status didn't flip or the link is broken.
   *  The row is shown anyway (graveyard completeness) and flagged for a look. */
  statusAnomaly: z.boolean(),

  // Panel-detail lead-context fields (Aesthetic Parity, Phase 2) — the joined
  // lead's transfer flag, received date, case file, and FSA document, defined
  // once in PANEL_DETAIL_LEAD_FIELDS. Additive (ADR-0133). On an anomaly row
  // (lead unresolved) the composer supplies the empty defaults.
  ...PANEL_DETAIL_LEAD_FIELDS,

  // Class-B placement fields (slice 4 — panel detail). The final filing
  // deadline + the closed case's monthly-update history, read-only here.
  ...PLACEMENT_DETAIL_FIELDS,
});
export type ClosedCase = z.infer<typeof ClosedCaseSchema>;

/**
 * Payload for `GET /api/closed`. `cases` is the full closed book, sorted
 * most-recently-closed first (newest Case Closed Date on top). A case with no
 * Case Closed Date (an anomaly) sorts last.
 */
export const ClosedResponseSchema = z.object({
  cases: z.array(ClosedCaseSchema),
});
export type ClosedResponse = z.infer<typeof ClosedResponseSchema>;
